import { Tag as AntTag, TagProps as AntTagProps, theme } from 'antd';
import styled from 'styled-components';
import type { GlobalToken } from 'antd';
import {
  ComponentSize,
  getControlHeight,
  getFontSize,
} from '../../utils/tokenHelpers';

// Tag only supports s, m, l (no xs)
export type TagSize = 's' | 'm' | 'l';

export interface TagProps extends Omit<AntTagProps, 'color'> {
  /** Tag size */
  size?: TagSize;
  /** Whether the tag is selected */
  selected?: boolean;
  /** Whether the tag is disabled */
  disabled?: boolean;
}

const getTagHeight = (token: GlobalToken, size: TagSize): number => {
  // Map tag sizes to control heights
  const sizeMap: Record<TagSize, ComponentSize> = {
    s: 's',
    m: 's', // Tag m uses same height as s (32px)
    l: 'm', // Tag l uses control height m (40px)
  };
  return getControlHeight(token, sizeMap[size]);
};

const getTagBorderRadius = (token: GlobalToken, size: TagSize): number => {
  const map: Record<TagSize, number> = {
    s: token.borderRadiusSM, // 4px
    m: token.borderRadius, // 8px
    l: token.borderRadius, // 8px
  };
  return map[size];
};

const getTagFontSize = (token: GlobalToken, size: TagSize): number => {
  const sizeMap: Record<TagSize, ComponentSize> = {
    s: 's',
    m: 's',
    l: 'm',
  };
  return getFontSize(token, sizeMap[size]);
};

interface StyledTagProps {
  $token: GlobalToken;
  $height: number;
  $fontSize: number;
  $borderRadius: number;
  $selected: boolean;
  $disabled: boolean;
}

const StyledTag = styled(AntTag)<StyledTagProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: ${({ $token }) => $token.fontFamily};
  font-weight: ${({ $token }) => $token.fontWeightStrong};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  transition: all ${({ $token }) => $token.motionDurationMid} ease;
  margin: 0;

  /* Size */
  height: ${({ $height }) => $height}px;
  padding: ${({ $token }) => `${$token.paddingXS}px ${$token.paddingSM}px`};
  font-size: ${({ $fontSize }) => $fontSize}px;
  border-radius: ${({ $borderRadius }) => $borderRadius}px;

  /* Disabled state */
  ${({ $disabled, $token }) =>
    $disabled &&
    `
    background: ${$token.colorBgContainerDisabled} !important;
    border-color: transparent !important;
    color: ${$token.colorTextDisabled} !important;
  `}

  /* Selected state */
  ${({ $selected, $disabled, $token }) =>
    !$disabled &&
    $selected &&
    `
    background: transparent;
    border: ${$token.lineWidth}px solid ${$token.colorPrimary};
    color: ${$token.colorPrimary};
    
    &:hover {
      background: ${$token.colorPrimaryHover} !important;
      border-color: ${$token.colorPrimaryHover} !important;
      color: ${$token.colorWhite} !important;
    }
  `}

  /* Unselected state */
  ${({ $selected, $disabled, $token }) =>
    !$disabled &&
    !$selected &&
    `
    background: transparent;
    border: ${$token.lineWidth}px solid ${$token.colorPrimary};
    color: ${$token.colorTextSecondary};
    
    &:hover {
      background: ${$token.colorPrimaryHover} !important;
      border-color: ${$token.colorPrimary} !important;
      color: ${$token.colorWhite} !important;
    }
  `}
`;

/**
 * Tag component based on Figma Design System
 *
 * Now uses Ant Design theme tokens for consistent styling and size support.
 *
 * @example
 * ```tsx
 * import { Tag } from '@libertify/ui';
 *
 * <Tag size="m">Default Tag</Tag>
 * <Tag size="m" selected>Selected Tag</Tag>
 * <Tag size="l" disabled>Disabled Tag</Tag>
 * <Tag size="s">Small Tag</Tag>
 * ```
 */
export const Tag = ({
  size = 'm',
  selected = false,
  disabled = false,
  children,
  ...props
}: TagProps) => {
  const { token } = theme.useToken();
  const height = getTagHeight(token, size);
  const fontSize = getTagFontSize(token, size);
  const borderRadius = getTagBorderRadius(token, size);

  return (
    <StyledTag
      $token={token}
      $height={height}
      $fontSize={fontSize}
      $borderRadius={borderRadius}
      $selected={selected}
      $disabled={disabled}
      {...props}
    >
      {children}
    </StyledTag>
  );
};

export default Tag;
